import { Button, LinearProgress, makeStyles, Snackbar, Theme, Typography } from '@material-ui/core'
import { Alert, AlertTitle } from '@material-ui/lab'
import React, { useEffect, useState } from 'react'
import { ErpTransferStatisticLine } from 'models/erp-transfer-statistic'
import { useDispatch, useSelector } from 'react-redux'
import { actionSetTransferFinished, setErrorSnackbarOpen } from '../../redux/dashboard/erp-statistic-actions'
import { AppState } from 'redux/app/app-store'
import { toShortDate, toTime } from 'utils/time/timeUtil'
import { Interval } from 'luxon'
import { tr } from 'utils/translations/translate'
import {
    TEXT_CLOSE,
    TEXT_ERP_TRANSFER_COMPLETE_MESSAGE,
    TEXT_ERP_TRANSFER_COMPLETE_TIME,
    TEXT_ERP_TRANSFER_COMPLETE_TITLE,
    TEXT_ERP_TRANSFER_ERROR,
    TEXT_ERP_TRANSFER_ERROR_MESSAGE,
    TEXT_ERP_TRANSFER_IN_PROGRESS,
    TEXT_ERP_TRANSFER_IN_PROGRESS_PRODUCT_COUNT_MESSAGE,
    TEXT_ERP_TRANSFER_IN_PROGRESS_REMAINING_TIME,
    TEXT_ERP_TRANSFER_IN_PROGRESS_TITLE,
    TEXT_HOURS,
    TEXT_MINUTES,
} from 'utils/translations/keys'

const useStyles = makeStyles((theme: Theme) => ({
    alertBoxInProgress: {
        color: 'rgba(0, 0, 0, 0.6)',
        backgroundColor: '#e6f4fb',
        marginBottom: 16,
        display: 'flex',
    },
    alertBoxContainer: {
        margin: '0 8px 16px 8px',
        width: '60%',
    },

    alertBoxItem: {
        flex: '1',
        width: '100%',
    },
    alertBoxError: {
        color: 'rgba(0, 0, 0, 0.6)',
        backgroundColor: '#fbebeb',
        marginBottom: 16,
    },
    alertBoxCloseButton: {
        width: 100,
        float: 'right',
    },
    alertMessage: {
        width: '100%',
    },
    progressBar: {
        marginTop: 10,
        marginBottom: 10,
        height: 6,
    },
}))

export type ErpTransferProps = {
    erpStatistic: ErpTransferStatisticLine
    sendToErpRunning: boolean
}

const ErpTransferProgressNotification: React.FC<ErpTransferProps> = (props) => {
    const { erpStatistic, sendToErpRunning } = props
    const styles = useStyles()
    const dispatch = useDispatch()
    const transferComplete = erpStatistic.endDate && !erpStatistic.isFinished
    const showSnackbarError = useSelector((state: AppState) => state.erpTransferStatisticState.showSnackbarError)
    const [progress, setProgress] = useState(0)
    const tenantConfig = useSelector((state: AppState) => state.config.tenantConfig)

    const setTransferFinished = () => {
        dispatch(actionSetTransferFinished(erpStatistic))
    }

    useEffect(() => {
        setProgress((oldProgress) => {
            return (erpStatistic.productsProcessed / erpStatistic.productCount) * 100
        })
    }, [erpStatistic, sendToErpRunning])

    const handleCloseErrorSnackbar = () => {
        dispatch(setErrorSnackbarOpen(false))
    }

    const getEstimatedTimeFormat = () => {
        const approxSingleInvoiceProcessingTime = 1 //1 sec
        const processSeconds =
            (erpStatistic.invoiceCount - erpStatistic.invoicesProcessed) * approxSingleInvoiceProcessingTime
        const startDate = new Date()
        const endDate = new Date()
        endDate.setSeconds(startDate.getSeconds() + processSeconds)
        const interval = Interval.fromDateTimes(startDate, endDate).toDuration(['hours', 'minutes']).toObject()
        const time = `${interval.hours && interval.hours > 0 ? `${interval.hours} ${tr(TEXT_HOURS)} ` : ''}${
            interval.minutes && interval.minutes > 1 ? Math.floor(interval.minutes) : 1} ${tr(TEXT_MINUTES)}`

        return tr(TEXT_ERP_TRANSFER_IN_PROGRESS_REMAINING_TIME, [ { name: 'time', value : time } ])
    }

    return transferComplete && erpStatistic.isError ? (
        <div className={styles.alertBoxContainer}>
            <Alert variant="standard" severity="error" className={styles.alertBoxError}>
                <AlertTitle>
                    <strong>{tr(TEXT_ERP_TRANSFER_ERROR)}</strong>
                </AlertTitle>
                <Typography>
                    <i>{erpStatistic.errorMessage}</i>
                </Typography>
                <Typography>{tr(TEXT_ERP_TRANSFER_ERROR_MESSAGE)}</Typography>
            </Alert>
            <Snackbar open={showSnackbarError && erpStatistic.isError} onClose={handleCloseErrorSnackbar}>
                <Alert onClose={handleCloseErrorSnackbar} severity="error" variant="filled">
                    {tr(TEXT_ERP_TRANSFER_ERROR)}
                </Alert>
            </Snackbar>
        </div>
    ) : transferComplete ? (
        <div className={styles.alertBoxContainer}>
            <Alert
                variant="standard"
                severity="info"
                className={styles.alertBoxInProgress}
                classes={{ message: styles.alertMessage }}
            >
                <AlertTitle className={styles.alertBoxItem}>
                    <strong>{tr(TEXT_ERP_TRANSFER_COMPLETE_TITLE)}</strong>
                    <Button
                        variant="text"
                        color="primary"
                        onClick={setTransferFinished}
                        className={styles.alertBoxCloseButton}
                    >
                        {tr(TEXT_CLOSE)}
                    </Button>
                </AlertTitle>
                <div>
                    <Typography>
                        {tr(TEXT_ERP_TRANSFER_COMPLETE_TIME, [
                            { name: 'date', value: toShortDate(erpStatistic.endDate) },
                            { name: 'time', value: toTime(erpStatistic.endDate) },
                        ])}
                    </Typography>
                </div>
                <div>
                    <Typography>{tr(TEXT_ERP_TRANSFER_COMPLETE_MESSAGE)}</Typography>
                </div>
            </Alert>
        </div>
    ) : (
        <div className={styles.alertBoxContainer}>
            <Alert
                variant="standard"
                severity="info"
                className={styles.alertBoxInProgress}
                classes={{ message: styles.alertMessage }}
            >
                <AlertTitle className={styles.alertBoxItem}>
                    <strong>
                        {tr(TEXT_ERP_TRANSFER_IN_PROGRESS_TITLE, [
                            { name: 'invoiceSystem', value: tenantConfig?.invoiceSystem ?? '' },
                        ])}
                    </strong>
                </AlertTitle>
                <Typography>{tr(TEXT_ERP_TRANSFER_IN_PROGRESS)}</Typography>
                <LinearProgress variant="determinate" value={progress} className={styles.progressBar}></LinearProgress>
                <Typography>{getEstimatedTimeFormat()}</Typography>
                <Typography>
                    {tr(TEXT_ERP_TRANSFER_IN_PROGRESS_PRODUCT_COUNT_MESSAGE, [
                        { name: 'productsSent', value: erpStatistic.productsSent },
                        { name: 'productCount', value: erpStatistic.productCount },
                    ])}
                </Typography>
            </Alert>
        </div>
    )
}

export default ErpTransferProgressNotification
